import { useEffect, useState } from 'react';
import { pathsApi, type StudyPlanCourse, type StudyPlanProgress } from '../lib/api';

const STATUS_LABELS: Record<string, string> = {
  LOCKED: 'Verrouillé',
  AVAILABLE: 'Disponible',
  IN_PROGRESS: 'En cours',
  PASSED: 'Validé',
  FAILED: 'Échoué',
};

function money(minor: number, currency: string) {
  return `${(minor / 100).toFixed(2)} ${currency}`;
}

export function StudyPathPage() {
  const [programVersionId, setProgramVersionId] = useState('');
  const [planId, setPlanId] = useState('');
  const [courses, setCourses] = useState<StudyPlanCourse[]>([]);
  const [progress, setProgress] = useState<StudyPlanProgress | null>(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => { if (planId) reload(planId); }, [planId]);

  async function reload(id: string) {
    try {
      const [c, p] = await Promise.all([pathsApi.courses(id), pathsApi.progress(id)]);
      setCourses(c); setProgress(p);
    } catch (e: any) { setError(String(e.message || e)); }
  }

  async function createPlan() {
    setError(''); setMessage('');
    try {
      const plan = await pathsApi.createPlan(programVersionId.trim());
      setPlanId(plan.id);
      setMessage('Parcours créé. Les cours sans prérequis sont déjà disponibles.');
    } catch (e: any) { setError(String(e.message || e)); }
  }

  async function complete(courseVersionId: string) {
    setError('');
    try {
      await pathsApi.complete(planId, courseVersionId, 80);
      await reload(planId);
    } catch (e: any) { setError(String(e.message || e)); }
  }

  return <section className="page">
    <header className="page-header">
      <div>
        <span className="eyebrow">PARCOURS AUTONOME</span>
        <h1>Étudier sans établissement</h1>
        <p>Choisissez un programme, suivez les cours dans l'ordre des prérequis. L'attestation est délivrée automatiquement au terme.</p>
      </div>
      {progress && <span className={`status ${progress.eligible_for_credential ? 'ok' : 'warn'}`}>
        {progress.passed_courses}/{progress.total_courses} validés
      </span>}
    </header>

    {error && <div className="alert error">{error}</div>}
    {message && <div className="notice">{message}</div>}

    {!planId && <div className="card">
      <h3>Ouvrir un parcours</h3>
      <label>Identifiant du programme
        <input value={programVersionId} onChange={(e: any) => setProgramVersionId(e.target.value)} placeholder="program_version_id" />
      </label>
      <button className="button primary" disabled={!programVersionId.trim()} onClick={createPlan}>Créer mon parcours</button>
      <p className="technical-note">Vous pouvez aussi reprendre un parcours existant en saisissant son identifiant ci-dessous.</p>
      <label>Reprendre un parcours
        <input value={planId} onChange={(e: any) => setPlanId(e.target.value)} placeholder="plan_id" />
      </label>
    </div>}

    {progress && <div className="card">
      <h3>Progression</h3>
      <div className="progress-bar"><span style={{ width: `${progress.total_courses ? (progress.passed_courses / progress.total_courses) * 100 : 0}%` }} /></div>
      <p>
        {progress.passed_courses} cours validés sur {progress.total_courses} ·
        coût total {money(progress.total_cost_minor, progress.currency)}
      </p>
      {progress.issued_credential_id
        ? <p className="success-note">Attestation délivrée. Elle est vérifiable publiquement par son numéro de série.</p>
        : progress.eligible_for_credential
          ? <p className="technical-note">Tous les cours sont validés ; l'attestation est en cours de signature.</p>
          : <p className="technical-note">L'attestation sera délivrée dès que tous les cours seront validés.</p>}
    </div>}

    {courses.length > 0 && <div className="card">
      <h3>Cours du parcours</h3>
      <p className="technical-note">Un cours verrouillé le reste tant que ses prérequis ne sont pas acquis.</p>
      {courses.map((c, i) => <div key={c.id} className={`plan-course status-${c.status.toLowerCase()}`}>
        <div>
          <strong>{i + 1}. {c.course_title || c.course_version_id.slice(0, 8)}</strong>
          <small>{STATUS_LABELS[c.status] || c.status}{c.score != null ? ` · note ${c.score}` : ''}</small>
        </div>
        {c.status === 'AVAILABLE' && <button className="button primary" onClick={() => complete(c.course_version_id)}>Valider ce cours</button>}
        {c.status === 'LOCKED' && <span className="lock">verrouillé</span>}
        {c.status === 'PASSED' && <span className="done">validé</span>}
      </div>)}
    </div>}
  </section>;
}
